import { DocumentNode } from "graphql";
import {
  GraphQLFetchClient,
  QueryResult,
  MutationResult,
  QueryResultPromise,
  MutationResultPromise,
  MutationOptions,
} from "modules/graphql-fetch";
import {
  validateMindboxCard,
  RegionsQuery,
  SuggestRegionQuery,
  SuggestAddressQuery,
  isRegisteredQuery,
  getProfileQuery,
  CategoriesQuery,
  SettingsQuery,
  DetectRegionQuery,
  HomeListingsQuery,
  SearchHelpQuery,
  BrowsingHistoryQuery,
  GoodsOfDay,
  MetaInformationQuery,
  CartQuery,
  AutocompleteQuery,
  RegionQuery,
  ProductsSearchQuery,
  FiltersConfigQuery,
  BannersQuery,
  PharmaciesOnMapQuery,
  PharmaciesListQuery,
  ProductInstructionsQuery,
  PrescriptionsQuery,
  PharmacyCardQuery,
  GroupQuery,
  BrandQuery,
  LoyaltiesQuery,
  DeliveryZonesQuery,
  AddressCoordinatesQuery,
  TeamPersonsQuery,
  RelatedProductsQuery,
  PreOrderQuery,
  PharmacySearchMapQuery,
  PickupStoreQuery,
  PharmacySearchListQuery,
  PharmacySearchHintsQuery,
  OrderQuery,
  updateFavoritePharmacy,
  OrderByPhonePageQuery,
  WidgetsQuery,
  CatalogPageQuery,
  PharmacyPageQuery,
  PromoPageQuery,
  userLastOrderQuery,
  PrescriptionQuery,
} from "./queries";
import {
  getCodeMutation,
  updateProfileMutation,
  loginMutation,
  registerMutation,
  logoutMutation,
  updateFavorites,
  UpdateCartMutation,
  ResetCartMutation,
  AddPromotedEmailMutation,
  ReguestCallbackMutation,
  createReviewMutation,
  setDrugNotifyMutation,
  UpdatePreOrderMutation,
  SaveOrderMutation,
  ApplyLoyaltyProgramMutation,
  CancelOrderMutation,
  ResetPreOrderMutation,
  refreshTokenMutation,
  loginOAuthMutation,
  connectOAuthMutation,
} from "./mutations";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type WithRegionID<T = any> = T & {
  regionID: Entity.ID;
};

export function factory(client: GraphQLFetchClient) {
  async function handleQuery<T, I = unknown>(
    query: DocumentNode,
    variables?: I,
    options?: MutationOptions
  ): Promise<QueryResult<T>> {
    try {
      return await client.query<T>({
        query,
        variables,
        options,
      });
    } catch (err) {
      return {
        data: null,
        errors: err.networkError
          ? [err.networkError]
          : err.graphQLErrors || [err],
      };
    }
  }

  async function handleMutation<T>(
    mutation,
    variables?,
    options?
  ): Promise<MutationResult<T>> {
    try {
      return await client.mutate<T>({
        mutation,
        variables,
        options,
      });
    } catch (err) {
      return {
        data: null,
        errors: err.networkError
          ? [err.networkError]
          : err.graphQLErrors || [err],
      };
    }
  }

  return {
    query: handleQuery,
    mutate: handleMutation,
    orderByPhone: {
      page(
        variables: Api.Meta.WithPageMeta.Input<{
          pharmacistPaginator: Api.Team.TeamPersonsInput["paginator"];
        }>
      ): QueryResultPromise<
        Api.Meta.WithPageMeta<{
          settings: Entity.Settings;
          teamPersons: Api.Result<{
            total: number;
            items: Array<Entity.Person.Pharmacist>;
          }>;
          teamInfo: Api.Result<{
            pharmacists: {
              description: string;
            };
          }>;
        }>
      > {
        return handleQuery(OrderByPhonePageQuery, variables);
      },
    },
    settings: {
      regions(): Promise<QueryResult<Api.Settings.Regions<"regions">>> {
        return handleQuery(RegionsQuery);
      },
      main(variables: WithRegionID): Promise<QueryResult<Api.Settings.Result>> {
        return handleQuery(SettingsQuery, variables);
      },
      detectRegion(
        variables: Api.Settings.Region.InputByIp
      ): Promise<QueryResult<Api.Settings.Region<"detectRegion">>> {
        return handleQuery(DetectRegionQuery, variables);
      },
      region(
        variables: Api.Settings.Region.Input
      ): Promise<QueryResult<Api.Settings.Region<"region">>> {
        return handleQuery(RegionQuery, variables);
      },
    },
    search: {
      suggestRegion(
        variables: Api.Search.Regions.Suggestions.Input
      ): Promise<QueryResult<Api.Search.Regions.Suggestions>> {
        return handleQuery(SuggestRegionQuery, variables);
      },
      suggestAddress(
        variables: Api.Search.SuggestAddress.Input
      ): QueryResultPromise<Api.Search.SuggestAddress.Result> {
        return handleQuery(SuggestAddressQuery, variables);
      },
      searchAutocomplete(variables) {
        return handleQuery(AutocompleteQuery, variables);
      },
      searchHelp(): QueryResultPromise<{
        searchHelp: Array<Entity.SearchAdvice>;
      }> {
        return handleQuery(SearchHelpQuery);
      },
      addressCoordinates(
        variables: Api.WithRegionID<Api.AddressCoordinates.Input>
      ): Promise<QueryResult<Api.AddressCoordinates.Result>> {
        return handleQuery(AddressCoordinatesQuery, variables);
      },
    },
    auth: {
      getCode(
        variables: Api.User.GetCodeInput,
        options
      ): Promise<MutationResult<Api.User.GetCodeResult>> {
        return handleMutation(getCodeMutation, variables, options);
      },
      isRegistered(
        variables: Api.User.IsRegisteredInput
      ): Promise<QueryResult<Api.User.IsRegisteredResult>> {
        return handleQuery(isRegisteredQuery, variables);
      },
      login(
        variables: Api.User.LoginInput
      ): Promise<MutationResult<Api.User.LoginResult>> {
        return handleMutation(loginMutation, variables);
      },
      loginOAuth(
        variables: Api.User.LoginOAuthInput
      ): Promise<MutationResult<Api.User.LoginOAuthResult>> {
        return handleMutation(loginOAuthMutation, variables);
      },
      register(
        variables: Api.User.RegisterInput
      ): Promise<MutationResult<Api.User.RegisterResult>> {
        return handleMutation(registerMutation, variables);
      },
      logout() {
        return handleMutation(logoutMutation);
      },
      validateMindboxCard(
        variables: Api.User.ValidateCardInput
      ): Promise<QueryResult<Api.User.ValidateCardResult>> {
        return handleQuery(validateMindboxCard, variables);
      },
      refreshToken(variables: {
        refreshToken: string;
      }): Promise<MutationResult> {
        return handleMutation(refreshTokenMutation, variables);
      },
    },
    profile: {
      getProfile(): Promise<QueryResult<Api.User.ProfileResult>> {
        return handleQuery(getProfileQuery);
      },
      updateProfile(
        variables: Api.User.UpdateProfileInput
      ): Promise<MutationResult<Api.User.UpdateProfileResult>> {
        return handleMutation(updateProfileMutation, variables);
      },
      connectOAuth(
        variables: Api.User.ConnectOAuthInput
      ): Promise<MutationResult<Api.User.ConnectOAuthResult>> {
        return handleMutation(connectOAuthMutation, variables);
      },
      addPromotedEmail(
        variables: Api.User.AddPromotedEmail.Input
      ): Promise<MutationResult<Api.User.AddPromotedEmail.Result>> {
        return handleMutation(AddPromotedEmailMutation, variables);
      },
    },
    catalog: {
      categories(
        variables: WithRegionID
      ): Promise<QueryResult<Api.Category.List>> {
        return handleQuery(CategoriesQuery, variables);
      },
      page(
        variables: Api.Meta.WithPageMeta.Input<Api.Product.ProductSearchInput>
      ): QueryResultPromise<Api.Product.ProductsResult & Api.Meta> {
        return handleQuery(CatalogPageQuery, variables);
      },
    },
    loyalty: {
      loyalties(): Promise<QueryResult<Api.Loyalty.Result>> {
        return handleQuery(LoyaltiesQuery);
      },
      applyLoyaltyProgram(
        variables: Api.ApplyLoyaltyProgram.Input
      ): MutationResultPromise<Api.ApplyLoyaltyProgram.Result> {
        return handleMutation(ApplyLoyaltyProgramMutation, variables);
      },
    },
    products: {
      filtersConfig(
        variables: Api.Product.FiltersConfigInput
      ): Promise<QueryResult<Api.Product.FiltersConfigResult>> {
        return handleQuery(FiltersConfigQuery, variables);
      },
      search(
        variables: Api.Product.ProductSearchInput
      ): Promise<QueryResult<Api.Product.ProductsResult>> {
        return handleQuery(ProductsSearchQuery, variables);
      },
      browsingHistory(
        variables: WithRegionID<{ ids: Array<Entity.ID> }>
      ): Promise<QueryResult<Api.Product.ProductsResult>> {
        return handleQuery(BrowsingHistoryQuery, variables);
      },
      goodsOfDay(
        variables: WithRegionID
      ): Promise<QueryResult<Api.Product.GoodsOfDay>> {
        return handleQuery(GoodsOfDay, variables);
      },
      prescriptions(
        variables: WithRegionID
      ): Promise<QueryResult<Api.User.Prescription>> {
        return handleQuery(PrescriptionsQuery, variables);
      },
      relatedProducts(
        variables: WithRegionID<{ categoryID: Entity.ID }>
      ): Promise<QueryResult<Api.Product.RelatedProducts>> {
        return handleQuery(RelatedProductsQuery, variables);
      },
    },
    metaInformation: {
      get(
        variables: WithRegionID<{ url: string }>
      ): QueryResultPromise<Api.Meta> {
        return handleQuery(MetaInformationQuery, variables);
      },
    },
    widgets: {
      get: (
        variables: Api.Widget.Input
      ): QueryResultPromise<Api.Widget.Result> => {
        return handleQuery(WidgetsQuery, variables);
      },
    },
    product: {
      createReview(
        variables: Api.Product.Review.Input
      ): Promise<MutationResult<Api.Product.Review.CreateResult>> {
        return handleMutation(createReviewMutation, variables);
      },
      setDrugNotify(variables) {
        return handleMutation(setDrugNotifyMutation, variables);
      },
      getInstructions(variables): Promise<QueryResult> {
        return handleQuery(ProductInstructionsQuery, variables);
      },
    },
    home: {
      page(
        variables: Api.Meta.WithPageMeta.Input<{
          articleSearch: Api.Blog.Articles.Input["search"];
          categoriesSearch: Api.Blog.Categories.Input["search"];
          bannerType: Api.Banners.BannerType;
        }>
      ): QueryResultPromise<
        {
          partners: Array<Entity.Partner>;
          mainSlider: Array<Entity.TopSliderSlide | Entity.TopSliderMainSlide>;
          seasonSelections: Array<Entity.SeasonalSelection>;
          drugsAlphabet: Entity.Drugs.Alphabet;
          brandsAlphabet: Entity.Drugs.Alphabet;
          diseasesAlphabet: Entity.Drugs.Alphabet;
        } & Api.Banners.Result &
          Api.Blog.Articles &
          Api.Blog.Categories &
          Api.Meta
      > {
        return handleQuery(HomeListingsQuery, variables);
      },
    },
    favorites: {
      add(
        variables: Api.Favorites.UpdateFavoritesInput
      ): Promise<MutationResult<Api.Favorites.UpdateFavoritesResult>> {
        return handleMutation(updateFavorites, variables);
      },
    },
    cart: {
      get(variables: Api.WithRegionID): QueryResultPromise<Api.Cart.Result> {
        return handleQuery(CartQuery, variables);
      },
      update(
        variables: Api.Cart.Input
      ): MutationResultPromise<Api.Cart.UpdateResult> {
        return handleMutation(UpdateCartMutation, variables);
      },
      reset(variables): Promise<MutationResult> {
        return handleMutation(ResetCartMutation, variables);
      },
    },
    preOrder: {
      get(
        variables: Api.WithRegionID<{ step?: Entity.PreOrder.StepOrderType }>,
        options?: MutationOptions
      ): QueryResultPromise<Api.PreOrder.Result> {
        return handleQuery(PreOrderQuery, variables, options);
      },
      update(
        variables: Api.UpdatePreOrder.Input,
        options?: MutationOptions
      ): MutationResultPromise<Api.UpdatePreOrder.Result> {
        return handleMutation(UpdatePreOrderMutation, variables, options);
      },
      reset(
        variables: Api.WithRegionID,
        options?: MutationOptions
      ): MutationResultPromise<Api.PreOrder.Reset.Result> {
        return handleMutation(ResetPreOrderMutation, variables, options);
      },
    },
    order: {
      get(variables: Api.Order.Input): QueryResultPromise<Api.Order> {
        return handleQuery(OrderQuery, variables);
      },
      getLast(): QueryResultPromise<Api.Order.LastOrder> {
        return handleQuery(userLastOrderQuery);
      },
      save(
        variables: Api.WithRegionID,
        options?: MutationOptions
      ): MutationResultPromise<Api.Order.Save.Result> {
        return handleMutation(SaveOrderMutation, variables, options);
      },
      cancel(
        variables: Api.Order.Cancel.Input
      ): MutationResultPromise<Api.Order.Cancel.Result> {
        return handleQuery(CancelOrderMutation, variables);
      },
    },
    pharmacies: {
      map(variables: {
        xMin: number;
        xMax: number;
        yMin: number;
        yMax: number;
        zoom: number;
        search: Array<string>;
        pharmacyIds: Array<Entity.ID>;
      }): Promise<
        QueryResult<{
          pharmacyMap: Array<Entity.Pharmacy | Entity.PharmacyCluster>;
        }>
      > {
        return handleQuery<{
          pharmacyMap: Array<Entity.Pharmacy | Entity.PharmacyCluster>;
        }>(PharmaciesOnMapQuery, variables);
      },
      list(variables): Promise<
        QueryResult<{
          searchPharmacy: { items: Array<Entity.Pharmacy>; total: number };
        }>
      > {
        return handleQuery<{
          searchPharmacy: { items: Array<Entity.Pharmacy>; total: number };
        }>(PharmaciesListQuery, variables);
      },
      searchMap(
        variables: Api.Pharmacy.SearchMap.Input,
        options?: MutationOptions
      ): QueryResultPromise<Api.Pharmacy.SearchMap.Result> {
        return handleQuery(PharmacySearchMapQuery, variables, options);
      },
      /* todo: type it */
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      searchList(variables, options?: MutationOptions): any {
        return handleQuery(PharmacySearchListQuery, variables, options);
      },
      /* todo: type it */
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      searchHints(variables, options?: MutationOptions): any {
        return handleQuery(PharmacySearchHintsQuery, variables, options);
      },
      pickupStore(
        variables: Api.WithRegionID<{ pharmacyId: Entity.ID }>,
        options?: MutationOptions
      ): QueryResultPromise<{ pickupStore: Entity.PreOrder.PickupStore }> {
        return handleQuery(PickupStoreQuery, variables, options);
      },
    },
    zone: {
      deliveryZones(
        variables: Api.WithRegionID
      ): QueryResultPromise<Api.Zone.DeliveryZones.Result> {
        return handleQuery(DeliveryZonesQuery, variables);
      },
    },
    pharmacy: {
      get(variables: { id: Entity.ID }): Promise<
        QueryResult<{
          pharmacy: Entity.PharmacyCard;
        }>
      > {
        return handleQuery<{
          pharmacy: Entity.PharmacyCard;
        }>(PharmacyCardQuery, variables);
      },
      updateFavorite(variables: Api.WithRegionID<{ id: Entity.ID }>): Promise<
        QueryResult<{
          updateFavoritePharmacy: Entity.PharmacyCard;
        }>
      > {
        return handleQuery(updateFavoritePharmacy, variables);
      },
      page(
        variables: Api.Meta.WithPageMeta.Input<{ id: Entity.ID }>
      ): QueryResultPromise<
        Api.Meta.WithPageMeta<{
          pharmacy: Entity.PharmacyCard;
        }>
      > {
        return handleQuery<
          Api.Meta.WithPageMeta<{
            pharmacy: Entity.PharmacyCard;
          }>
        >(PharmacyPageQuery, variables);
      },
    },
    adv: {
      banners(
        variables: Api.Banners.Input
      ): Promise<QueryResult<Api.Banners.Result>> {
        return handleQuery(BannersQuery, variables);
      },
    },
    team: {
      teamPersons(
        variables: Api.Team.TeamPersonsInput
      ): Promise<QueryResult<Api.Team.TeamPersonsResult>> {
        return handleQuery(TeamPersonsQuery, variables);
      },
    },
    group: {
      get(
        variables: Api.Group.Input
      ): Promise<QueryResult<Api.Group.GroupResult>> {
        return handleQuery(GroupQuery, variables);
      },
    },
    promo: {
      page(
        variables: Api.Promo.Page.Input
      ): Promise<QueryResult<Api.Promo.Page>> {
        return handleQuery(PromoPageQuery, variables);
      },
    },
    brand: {
      get(
        variables: Api.Meta.WithPageMeta.Input<{ alias: Entity.ID }>
      ): Promise<
        QueryResult<
          Api.Meta.WithPageMeta<{
            brand: Entity.Brand;
          }>
        >
      > {
        return handleQuery<
          Api.Meta.WithPageMeta<{
            brand: Entity.Brand;
          }>
        >(BrandQuery, variables);
      },
    },
    request: {
      callback(
        variables: Api.Reguest.Callback.Input,
        options
      ): Promise<MutationResult<Api.Reguest.Callback.Result>> {
        return handleMutation(ReguestCallbackMutation, variables, options);
      },
    },
    prescription: {
      get(
        variables: Api.Prescription.Input
      ): Promise<QueryResult<Api.Prescription.Result>> {
        return handleQuery(PrescriptionQuery, variables);
      },
    },
  };
}
